import React from "react";
import { memo } from "react";
import { Grid, TextField } from "@mui/material";
import { buscarEnderecoPorCep, mascararCEP } from "./fichaFormato";
import { inputSx } from "./fichaEstilos";

// ── Sub-componente: bloco de 7 campos de endereço ────────────────────────────

function BlocoEnderecoImpl({ fields, form, setField, ro, ehAprovador = false, obrigatorio = false, erroEfetivo, setErrosForm, layout = "padrao" }) {
  const vz = (f) => ehAprovador && !(form[f] ?? "");
  const erro = (f) => (obrigatorio && erroEfetivo ? erroEfetivo(f, form[f]) : undefined);
  const marcarErro = (f, msg) => {
    if (obrigatorio && !ro && !form[f]?.toString().trim()) setErrosForm?.((p) => ({ ...p, [f]: msg }));
  };
  const lbl = (texto) => (obrigatorio ? `${texto}*` : texto);

  const largo = layout === "largo";
  const tamCep = largo ? { xs: 12, sm: 2, md: 2 } : { xs: 12, sm: 3, md: 2 };
  const tamEndereco = largo ? { xs: 12, sm: 10, md: 10 } : { xs: 12, sm: 6, md: 4 };
  const tamNumero = largo ? { xs: 12, sm: 2, md: 2 } : { xs: 12, sm: 3, md: 2 };
  const tamComplemento = largo ? { xs: 12, sm: 5, md: 5 } : { xs: 8, sm: 10, md: 6 };
  const tamBairro = largo ? { xs: 12, sm: 5, md: 5 } : { xs: 12, sm: 6, md: 4 };
  const tamCidade = largo ? { xs: 12, sm: 10, md: 10 } : { xs: 12, sm: 6, md: 5 };
  const tamUf = largo ? { xs: 4, sm: 2, md: 2 } : { xs: 4, sm: 2, md: 1 };

  const campoCep = (
    <Grid size={tamCep}>
      <TextField
        label={lbl("CEP")}
        size="small"
        fullWidth
        value={form[fields.cep] ?? ""}
        placeholder="00000-000"
        onChange={async (e) => {
          if (ro) return;
          const cepFormatado = mascararCEP(e.target.value);
          setField(fields.cep, cepFormatado);
          if (obrigatorio) setErrosForm?.((p) => ({ ...p, [fields.cep]: undefined }));
          if (cepFormatado.replace(/\D/g, "").length === 8) {
            const dados = await buscarEnderecoPorCep(cepFormatado);
            if (dados) {
              if (dados.endereco) setField(fields.endereco, dados.endereco.toUpperCase());
              if (dados.bairro) setField(fields.bairro, dados.bairro.toUpperCase());
              if (dados.cidade) setField(fields.cidade, dados.cidade.toUpperCase());
              if (dados.uf) setField(fields.uf, dados.uf.toUpperCase());
              if (obrigatorio)
                setErrosForm?.((p) => ({ ...p, [fields.endereco]: undefined, [fields.bairro]: undefined, [fields.cidade]: undefined, [fields.uf]: undefined }));
            }
          }
        }}
        onBlur={() => marcarErro(fields.cep, "CEP é obrigatório.")}
        error={!!erro(fields.cep)}
        helperText={erro(fields.cep) || ""}
        slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 9 } }}
        sx={inputSx(ro, vz(fields.cep), !!erro(fields.cep))}
      />
    </Grid>
  );
  const campoEndereco = (
    <Grid size={tamEndereco}>
      <TextField
        label={lbl("Logradouro")}
        size="small"
        fullWidth
        value={form[fields.endereco] ?? ""}
        onChange={(e) => {
          if (!ro) {
            setField(fields.endereco, e.target.value.toUpperCase());
            if (obrigatorio) setErrosForm?.((p) => ({ ...p, [fields.endereco]: undefined }));
          }
        }}
        onBlur={() => marcarErro(fields.endereco, "Logradouro é obrigatório.")}
        error={!!erro(fields.endereco)}
        helperText={erro(fields.endereco) || ""}
        slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 255 } }}
        sx={{ ...inputSx(ro, vz(fields.endereco), !!erro(fields.endereco)), "& input": { color: "var(--ig-t-ffffff)", textTransform: "uppercase" } }}
      />
    </Grid>
  );
  const campoNumero = (
    <Grid size={tamNumero}>
      <TextField
        label={lbl("Número")}
        size="small"
        fullWidth
        value={form[fields.numero] ?? ""}
        onChange={(e) => {
          if (!ro) {
            setField(fields.numero, e.target.value.toUpperCase());
            if (obrigatorio) setErrosForm?.((p) => ({ ...p, [fields.numero]: undefined }));
          }
        }}
        onBlur={() => marcarErro(fields.numero, "Número é obrigatório.")}
        error={!!erro(fields.numero)}
        helperText={erro(fields.numero) || ""}
        slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 20 } }}
        sx={{ ...inputSx(ro, vz(fields.numero), !!erro(fields.numero)), "& input": { color: "var(--ig-t-ffffff)", textTransform: "uppercase" } }}
      />
    </Grid>
  );
  const campoComplemento = (
    <Grid size={tamComplemento}>
      <TextField
        label="Complemento"
        size="small"
        fullWidth
        value={form[fields.complemento] ?? ""}
        onChange={(e) => !ro && setField(fields.complemento, e.target.value.toUpperCase())}
        slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 100 } }}
        sx={{ ...inputSx(ro, vz(fields.complemento)), "& input": { color: "var(--ig-t-ffffff)", textTransform: "uppercase" } }}
      />
    </Grid>
  );
  const campoBairro = (
    <Grid size={tamBairro}>
      <TextField
        label={lbl("Bairro")}
        size="small"
        fullWidth
        value={form[fields.bairro] ?? ""}
        onChange={(e) => {
          if (!ro) {
            setField(fields.bairro, e.target.value.toUpperCase());
            if (obrigatorio) setErrosForm?.((p) => ({ ...p, [fields.bairro]: undefined }));
          }
        }}
        onBlur={() => marcarErro(fields.bairro, "Bairro é obrigatório.")}
        error={!!erro(fields.bairro)}
        helperText={erro(fields.bairro) || ""}
        slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 100 } }}
        sx={{ ...inputSx(ro, vz(fields.bairro), !!erro(fields.bairro)), "& input": { color: "var(--ig-t-ffffff)", textTransform: "uppercase" } }}
      />
    </Grid>
  );
  const campoCidade = (
    <Grid size={tamCidade}>
      <TextField
        label={lbl("Cidade")}
        size="small"
        fullWidth
        value={form[fields.cidade] ?? ""}
        onChange={(e) => {
          if (!ro) {
            setField(fields.cidade, e.target.value.toUpperCase());
            if (obrigatorio) setErrosForm?.((p) => ({ ...p, [fields.cidade]: undefined }));
          }
        }}
        onBlur={() => marcarErro(fields.cidade, "Cidade é obrigatória.")}
        error={!!erro(fields.cidade)}
        helperText={erro(fields.cidade) || ""}
        slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 100 } }}
        sx={{ ...inputSx(ro, vz(fields.cidade), !!erro(fields.cidade)), "& input": { color: "var(--ig-t-ffffff)", textTransform: "uppercase" } }}
      />
    </Grid>
  );
  const campoUf = (
    <Grid size={tamUf}>
      <TextField
        label={lbl("UF")}
        size="small"
        fullWidth
        value={form[fields.uf] ?? ""}
        onChange={(e) => {
          if (!ro) {
            setField(fields.uf, e.target.value.toUpperCase());
            if (obrigatorio) setErrosForm?.((p) => ({ ...p, [fields.uf]: undefined }));
          }
        }}
        onBlur={() => marcarErro(fields.uf, "UF é obrigatória.")}
        error={!!erro(fields.uf)}
        helperText={erro(fields.uf) || ""}
        slotProps={{ input: { readOnly: ro }, htmlInput: { maxLength: 2 } }}
        sx={inputSx(ro, vz(fields.uf), !!erro(fields.uf))}
      />
    </Grid>
  );

  return (
    <Grid container spacing={2}>
      {largo ? (
        <>
          {campoCep}
          {campoEndereco}
          {campoNumero}
          {campoComplemento}
          {campoBairro}
          {campoCidade}
          {campoUf}
        </>
      ) : (
        <>
          {campoCep}
          {campoEndereco}
          {campoNumero}
          {campoBairro}
          {campoCidade}
          {campoUf}
          {campoComplemento}
        </>
      )}
    </Grid>
  );
}

function mesmosCamposEndereco(prev, next) {
  if (
    prev.fields !== next.fields ||
    prev.setField !== next.setField ||
    prev.ro !== next.ro ||
    prev.ehAprovador !== next.ehAprovador ||
    prev.obrigatorio !== next.obrigatorio ||
    prev.erroEfetivo !== next.erroEfetivo ||
    prev.setErrosForm !== next.setErrosForm ||
    prev.layout !== next.layout
  )
    return false;
  const { cep, endereco, numero, bairro, cidade, uf, complemento } = prev.fields;
  return [cep, endereco, numero, bairro, cidade, uf, complemento].every((k) => prev.form[k] === next.form[k]);
}

export const BlocoEndereco = memo(BlocoEnderecoImpl, mesmosCamposEndereco);

export const FIELDS_END_PJ = { endereco: "endereco", numero: "numero", bairro: "bairro", cidade: "cidade", uf: "uf", cep: "cep", complemento: "complemento" };
export const FIELDS_END_FILIAL = {
  endereco: "enderecoFilial",
  numero: "numeroFilial",
  bairro: "bairroFilial",
  cidade: "cidadeFilial",
  uf: "ufFilial",
  cep: "cepFilial",
  complemento: "complementoFilial",
};
export const FIELDS_END_PF = {
  endereco: "pfEndereco",
  numero: "pfNumero",
  bairro: "pfBairro",
  cidade: "pfCidade",
  uf: "pfUf",
  cep: "pfCep",
  complemento: "pfComplemento",
};
export const FIELDS_END_PFEMP = {
  endereco: "pfEmpresaEndereco",
  numero: "pfEmpresaNumero",
  bairro: "pfEmpresaBairro",
  cidade: "pfEmpresaCidade",
  uf: "pfEmpresaUf",
  cep: "pfEmpresaCep",
  complemento: "pfEmpresaComplemento",
};
export const FIELDS_END_CJEMP = {
  endereco: "conjugeEmpresaEndereco",
  numero: "conjugeEmpresaNumero",
  bairro: "conjugeEmpresaBairro",
  cidade: "conjugeEmpresaCidade",
  uf: "conjugeEmpresaUf",
  cep: "conjugeEmpresaCep",
  complemento: "conjugeEmpresaComplemento",
};
